import { useRef, useCallback } from 'react';
import { Shape } from '../types/Shape';
import { useHistory } from './useHistory';

export interface UseCanvasHistoryProps {
  shapes: Shape[];
  setShapes: React.Dispatch<React.SetStateAction<Shape[]>>;
  history: ReturnType<typeof useHistory>;
}

export interface UseCanvasHistoryReturn {
  // Action handlers
  handleUndo: () => void;
  handleRedo: () => void;
  handleBeforeAction: () => void;

  // Action tracking refs
  isUserActionRef: React.MutableRefObject<boolean>;
  hasActionStartedRef: React.MutableRefObject<boolean>;

  // Utility
  pushHistoryIfNeeded: () => void;
  markActionComplete: () => void;
}

export function useCanvasHistory({
  shapes,
  setShapes,
  history,
}: UseCanvasHistoryProps): UseCanvasHistoryReturn {

  // Action tracking refs
  const isUserActionRef = useRef(true);
  const hasActionStartedRef = useRef(false);

  // Undo handler
  const handleUndo = useCallback(() => {
    if (history.canUndo()) {
      const prevShapes = history.undo();
      if (prevShapes) {
        isUserActionRef.current = false;
        setShapes(prevShapes);
        // Reset for next action
        setTimeout(() => {
          isUserActionRef.current = true;
        }, 0);
      }
    }
  }, [history, setShapes]);

  // Redo handler
  const handleRedo = useCallback(() => {
    if (history.canRedo()) {
      const nextShapes = history.redo();
      if (nextShapes) {
        isUserActionRef.current = false;
        setShapes(nextShapes);
        // Reset for next action
        setTimeout(() => {
          isUserActionRef.current = true;
        }, 0);
      }
    }
  }, [history, setShapes]);

  // Push history before action
  const handleBeforeAction = useCallback(() => {
    history.pushHistory(shapes);
  }, [history, shapes]);

  // Push history if action hasn't started yet
  const pushHistoryIfNeeded = useCallback(() => {
    if (!hasActionStartedRef.current) {
      history.pushHistory(shapes);
      hasActionStartedRef.current = true;
    }
  }, [history, shapes]);

  // Mark action as complete
  const markActionComplete = useCallback(() => {
    hasActionStartedRef.current = false;
  }, []);

  return {
    handleUndo,
    handleRedo,
    handleBeforeAction,
    isUserActionRef,
    hasActionStartedRef,
    pushHistoryIfNeeded,
    markActionComplete,
  };
}
